import { motion } from "framer-motion";
import { Flower2, HeartPulse, PersonStanding, Bike, Mountain, Target } from "lucide-react";

const scoreColors = {
  1: "#ff0080",
  2: "#ff0080",
  3: "#ff5500",
  4: "#ffaa00",
  5: "#ccff00",
  6: "#ccff00",
  7: "#00f2ea",
  8: "#00f2ea",
  9: "#00ff88",
  10: "#00ff88"
};

const ScoreBar = ({ score, maxScore = 10 }) => {
  const percentage = (score / maxScore) * 100;
  const color = scoreColors[score] || "#a1a1aa";
  
  return (
    <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${percentage}%` }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="absolute h-full rounded-full"
        style={{ backgroundColor: color }}
      />
    </div>
  );
};

const HealthCard = ({ icon: Icon, title, score, label, description, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-light rounded-lg p-2 space-y-1"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1">
        <Icon className="w-3 h-3" style={{ color }} />
        <span className="text-xs font-medium">{title}</span>
      </div>
      <span className="text-sm font-bold" style={{ color: scoreColors[score] }}>
        {score}/10
      </span>
    </div>
    <ScoreBar score={score} />
    <div className="text-xs text-white/60">{label}</div>
  </motion.div>
);

const ActivityCard = ({ icon: Icon, title, score, label, color }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="glass-light rounded-lg p-2 text-center min-w-0"
  >
    <div className="p-1.5 rounded-lg mx-auto w-fit mb-1" style={{ backgroundColor: `${color}20` }}>
      <Icon className="w-3 h-3" style={{ color }} />
    </div>
    <p className="text-[10px] sm:text-xs font-medium truncate">{title}</p>
    <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-1">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${(score / 10) * 100}%` }}
        transition={{ duration: 0.5 }}
        className="h-full rounded-full"
        style={{ backgroundColor: scoreColors[score] }}
      />
    </div>
  </motion.div>
);

export default function HealthForecast({ data }) {
  if (!data) return null;
  
  return (
    <div className="space-y-3">
      {/* Health Indices */}
      <div className="grid grid-cols-2 gap-2">
        <HealthCard
          icon={Flower2}
          title="Allergy Index"
          score={data.allergy?.score || 5}
          label={data.allergy?.label || "Moderate"}
          description={data.allergy?.description}
          color="#ccff00"
        />
        <HealthCard
          icon={HeartPulse}
          title="Flu Risk"
          score={data.flu?.score || 5}
          label={data.flu?.label || "Moderate"}
          description={data.flu?.description}
          color="#ff0080"
        />
      </div>
      <div className="rounded-lg bg-cyan-300/10 border border-cyan-300/15 px-3 py-2 text-[10px] text-cyan-200" data-testid="health-source-comparison-badge">
        Health source overlay: weather conditions + air-quality derived risk model
      </div>
      
      {/* Activity Conditions */}
      <div>
        <h4 className="text-xs font-medium text-white/60 mb-2 flex items-center gap-1">
          <Target className="w-3 h-3" />
          Activity Conditions
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
          <ActivityCard
            icon={PersonStanding}
            title="Running"
            score={data.activities?.running?.score || 5}
            label={data.activities?.running?.label || "Good"}
            color="#00f2ea"
          />
          <ActivityCard
            icon={Bike}
            title="Cycling"
            score={data.activities?.cycling?.score || 5}
            label={data.activities?.cycling?.label || "Good"}
            color="#ccff00"
          />
          <ActivityCard
            icon={Mountain}
            title="Hiking"
            score={data.activities?.hiking?.score || 5}
            label={data.activities?.hiking?.label || "Good"}
            color="#7000ff"
          />
          <ActivityCard
            icon={Target}
            title="Golf"
            score={data.activities?.golf?.score || 5}
            label={data.activities?.golf?.label || "Good"}
            color="#00ff88"
          />
        </div>
      </div>
    </div>
  );
}
